/**
 * themeStyles.js — shared, pure style-generation logic for buttons and status
 * badges, driven by the tenant's brand colors (app_config primary/secondary/
 * success/danger/warning/info) plus one style preset (`button_status_style`).
 * This is the SINGLE source of truth for how those colors turn into actual
 * button/pill styles — both ThemeContext.js (live app-wide tokens,
 * T.btnPrimary/T.btnGhost/T.btnDanger/T.statusPill) and ConfigMaster.js's
 * style-picker cards and Live Preview tab (unsaved draft form values) call
 * into these same functions, so what an admin sees before saving is
 * guaranteed to match what the rest of the app renders after saving.
 *
 * Every style is built ONLY from the tenant's own configured colors
 * (yellow/blueDark = primary brand, green/orange/red/blue = semantic status
 * colors) — never a hue outside that set. What changes between styles is the
 * material (flat fill, raised/pressed neumorphic surface, frosted glass,
 * faceted/angled geometry, warm bordered card, gradient spread, organic
 * shape), not the color palette itself.
 *
 * "solid" reproduces the app's original look exactly — the default, so
 * existing installs are visually unchanged unless an admin picks otherwise.
 */

function hexToRgb(hex) {
  try {
    const n = parseInt(String(hex).replace("#", ""), 16);
    return `${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff}`;
  } catch { return "0,0,0"; }
}

function darken(hex, amt = 20) {
  try {
    const n = parseInt(String(hex).replace("#", ""), 16);
    const r = Math.max(0, (n >> 16) - amt), g = Math.max(0, ((n >> 8) & 0xff) - amt), b = Math.max(0, (n & 0xff) - amt);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  } catch { return hex; }
}

function lighten(hex, amt = 40) {
  try {
    const n = parseInt(String(hex).replace("#", ""), 16);
    const r = Math.min(255, (n >> 16) + amt), g = Math.min(255, ((n >> 8) & 0xff) + amt), b = Math.min(255, (n & 0xff) + amt);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  } catch { return hex; }
}

export const STYLE_OPTIONS = [
  { value: "solid",         label: "Solid — bold fill",        description: "Bold solid-fill primary buttons and clearly-tinted status pills. The original i-Track look." },
  { value: "soft",          label: "Soft — tinted",            description: "Softer tinted/translucent buttons and lighter status pills. Calmer, lower-contrast feel." },
  { value: "outline",       label: "Outline — bordered",       description: "Transparent, bordered buttons and pills everywhere. Minimal, high-contrast-text look." },
  { value: "neumorphism",   label: "Neumorphism — soft UI",    description: "Buttons and pills share the page's own color — only light and shadow tell them apart. Raised = clickable, recessed = status." },
  { value: "glassmorphism", label: "Glassmorphism — frosted",  description: "Translucent, blurred panels with a glowing brand-color backdrop. Premium, layered, dashboard-forward." },
  { value: "polymorphism",  label: "Polymorphism — faceted",   description: "Angled, geometric button edges and a faceted mark. A distinctive, sports-tech identity." },
  { value: "vintage",       label: "Vintage Paper — warm",     description: "Warm, bordered, parchment-toned cards built from your own orange/yellow. Classic and trustworthy." },
  { value: "gradient",      label: "Gradient Vibe — energetic",description: "A vivid yellow-orange-red spread across buttons and headers. Young, dynamic, sports-platform energy." },
  { value: "nature",        label: "Nature Organic — calm",    description: "Rounded, organic shapes in green and warm earth tones. Friendly and approachable, less technical." },
];

// Elevation tiers shared by the flat/tinted/bordered family of styles — this
// is what makes a button read as "raised and clickable" versus a flat status
// pill. The material-driven styles (neumorphism, glassmorphism, polymorphism,
// vintage, gradient, nature) define their own shadow sets inline instead,
// since their elevation logic is part of what makes each one distinct.
const SHADOW = {
  solid:   { rest: "0 1px 3px rgba(0,0,0,0.20)", hover: "0 5px 12px rgba(0,0,0,0.26)", press: "0 1px 2px rgba(0,0,0,0.18)" },
  soft:    { rest: "0 1px 2px rgba(0,0,0,0.12)", hover: "0 4px 10px rgba(0,0,0,0.16)", press: "0 1px 1px rgba(0,0,0,0.10)" },
  outline: { rest: "none",                       hover: "0 3px 8px rgba(0,0,0,0.12)",  press: "none" },
};

/**
 * Button tokens for Primary / Ghost / Danger, given the brand colors and the
 * selected style. `isDark` adjusts neutrals only — the brand colors
 * themselves are the same in both themes (matches BRAND in ThemeContext.js).
 * Every token carries `borderRadius` and a `shadow`/`shadowHover`/
 * `shadowPress` triple (rendered by ThemedButton.js's real hover/press
 * state), plus optional `clipPath`/`backdropFilter`/`hoverBackground` for
 * styles that need them. Danger stays red in every style — only its
 * material (fill vs. border vs. glass) changes; the destructive signal never
 * moves to a different hue.
 */
export function buildButtonTokens({ yellow, blueDark, red, green, orange, blue, isDark, style }) {
  yellow = yellow || "#f7ce0b";
  blueDark = blueDark || "#02304f";
  red = red || "#dc2626";
  green = green || "#059669";
  orange = orange || "#d97706";
  blue = blue || "#034e7b";

  const sh = SHADOW[style] || SHADOW.solid;
  const withShadow = (t) => ({ borderRadius: 9, ...t, shadow: t.shadow || sh.rest, shadowHover: t.shadowHover || sh.hover, shadowPress: t.shadowPress || sh.press });

  const ghostSolidBg = isDark ? "rgba(255,255,255,0.12)" : "#eef2f7";
  const ghostText     = isDark ? "rgba(255,255,255,0.92)" : "#1e293b";

  if (style === "outline") {
    const ghostBorder = isDark ? "rgba(255,255,255,0.22)" : "rgba(3,78,123,0.28)";
    return {
      primary: withShadow({ background: "transparent", color: isDark ? yellow : darken(yellow, 50), border: `1.5px solid ${yellow}`, fontWeight: 800,
                             hoverBackground: `rgba(${hexToRgb(yellow)},0.14)` }),
      ghost:   withShadow({ background: "transparent", color: ghostText, border: `1px solid ${ghostBorder}`, fontWeight: 700,
                             hoverBackground: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.06)" }),
      danger:  withShadow({ background: "transparent", color: red, border: `1.5px solid ${red}`, fontWeight: 700,
                             hoverBackground: `rgba(${hexToRgb(red)},0.12)` }),
    };
  }
  if (style === "soft") {
    return {
      primary: withShadow({ background: `rgba(${hexToRgb(yellow)},0.28)`, color: isDark ? yellow : darken(yellow, 60), border: "none", fontWeight: 800 }),
      ghost:   withShadow({ background: isDark ? "rgba(255,255,255,0.09)" : "rgba(3,78,123,0.07)", color: ghostText, border: "none", fontWeight: 700 }),
      danger:  withShadow({ background: `rgba(${hexToRgb(red)},0.18)`, color: red, border: "none", fontWeight: 700 }),
    };
  }

  // ── Neumorphism — soft UI: page-colored surfaces, light+shadow only ─────
  // Every variant shares one raised surface; only text color tells them
  // apart. Pressing inverts to an inset shadow — the literal, physical
  // opposite of a status pill's permanently-recessed look (see
  // buildStatusPill below), which is what makes this style answer "is this
  // clickable?" without relying on color at all.
  if (style === "neumorphism") {
    const base = isDark ? "#1b2733" : "#eef1f4";
    const dark = isDark ? "rgba(0,0,0,0.55)" : "rgba(2,48,79,0.16)";
    const light = isDark ? "rgba(255,255,255,0.05)" : "#ffffff";
    const common = {
      background: base, border: "none", borderRadius: 12,
      shadow: `5px 5px 10px ${dark}, -5px -5px 10px ${light}`,
      shadowHover: `2px 2px 5px ${dark}, -2px -2px 5px ${light}`,
      shadowPress: `inset 3px 3px 6px ${dark}, inset -3px -3px 6px ${light}`,
    };
    return {
      primary: { ...common, color: isDark ? yellow : blueDark, fontWeight: 800 },
      ghost:   { ...common, color: ghostText, fontWeight: 700 },
      danger:  { ...common, color: red, fontWeight: 700 },
    };
  }

  // ── Glassmorphism — frosted panels over the brand's own navy/yellow glow ─
  if (style === "glassmorphism") {
    return {
      primary: withShadow({ background: `rgba(${hexToRgb(blueDark)},0.72)`, color: "#fff", border: `1px solid rgba(${hexToRgb(blueDark)},0.5)`, fontWeight: 800,
                             borderRadius: 11, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                             shadow: "0 4px 14px rgba(2,48,79,0.14)", shadowHover: "0 6px 20px rgba(2,48,79,0.20)", shadowPress: "0 2px 8px rgba(2,48,79,0.16)" }),
      ghost:   withShadow({ background: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.45)", color: isDark ? "rgba(255,255,255,0.92)" : blueDark,
                             border: `1px solid rgba(255,255,255,${isDark ? 0.15 : 0.6})`, fontWeight: 700,
                             borderRadius: 11, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                             shadow: "0 2px 8px rgba(2,48,79,0.08)", shadowHover: "0 4px 14px rgba(2,48,79,0.12)", shadowPress: "0 1px 4px rgba(2,48,79,0.10)" }),
      danger:  withShadow({ background: `rgba(${hexToRgb(red)},0.38)`, color: "#fff", border: "1px solid rgba(248,113,113,0.5)", fontWeight: 700,
                             borderRadius: 11, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                             shadow: "0 4px 14px rgba(220,38,38,0.16)", shadowHover: "0 6px 20px rgba(220,38,38,0.24)", shadowPress: "0 2px 8px rgba(220,38,38,0.18)" }),
    };
  }

  // ── Polymorphism — angled/faceted geometry, navy + yellow fills ─────────
  if (style === "polymorphism") {
    const clip = "polygon(4% 0%, 100% 0%, 96% 100%, 0% 100%)";
    const ghostColor = isDark ? yellow : blueDark;
    return {
      primary: withShadow({ background: `linear-gradient(135deg,${blueDark},${darken(blueDark, 10)})`, color: "#fff", border: "none", fontWeight: 800,
                             borderRadius: 8, clipPath: clip }),
      ghost:   withShadow({ background: "transparent", color: ghostColor, border: `1.5px solid ${ghostColor}`, fontWeight: 700,
                             borderRadius: 8, clipPath: clip, shadow: "none", shadowPress: "none",
                             hoverBackground: `rgba(${hexToRgb(ghostColor)},0.10)` }),
      danger:  withShadow({ background: `linear-gradient(135deg,${red},${darken(red, 20)})`, color: "#fff", border: "none", fontWeight: 700,
                             borderRadius: 8, clipPath: clip }),
    };
  }

  // ── Vintage Paper — warm parchment built from orange/yellow, navy ink ───
  if (style === "vintage") {
    const ghostBg = isDark ? "#241a10" : "#fbf1e0";
    const ghostColor = isDark ? lighten(orange, 25) : darken(orange, 25);
    return {
      primary: withShadow({ background: `linear-gradient(180deg,${orange},${darken(orange, 25)})`, color: "#fff8ea", border: `1px solid ${darken(orange, 35)}`, fontWeight: 800,
                             borderRadius: 6 }),
      ghost:   withShadow({ background: ghostBg, border: `1.5px solid ${orange}`, color: ghostColor, fontWeight: 700, borderRadius: 6 }),
      danger:  withShadow({ background: ghostBg, border: `1.5px solid ${red}`, color: red, fontWeight: 700, borderRadius: 6 }),
    };
  }

  // ── Gradient Vibe — vivid yellow→orange spread, red/navy for contrast ───
  if (style === "gradient") {
    const ghostBg = isDark ? "rgba(255,255,255,0.06)" : "#ffffff";
    return {
      primary: withShadow({ background: `linear-gradient(120deg,${yellow},${orange})`, color: blueDark, border: "none", fontWeight: 800, borderRadius: 10,
                             shadow: `0 4px 14px rgba(${hexToRgb(orange)},0.30)`, shadowHover: `0 6px 20px rgba(${hexToRgb(orange)},0.38)`, shadowPress: `0 2px 8px rgba(${hexToRgb(orange)},0.26)` }),
      ghost:   withShadow({ background: ghostBg, border: `1.5px solid ${orange}`, color: isDark ? lighten(orange, 20) : darken(orange, 15), fontWeight: 700, borderRadius: 10 }),
      danger:  withShadow({ background: ghostBg, border: `1.5px solid ${red}`, color: red, fontWeight: 700, borderRadius: 10 }),
    };
  }

  // ── Nature Organic — green primary, warm-orange secondary, pill shapes ──
  if (style === "nature") {
    const ghostBg = isDark ? "#16241c" : "#eaf3ee";
    return {
      primary: withShadow({ background: `linear-gradient(135deg,${green},${darken(green, 20)})`, color: "#fff", border: "none", fontWeight: 800, borderRadius: 20 }),
      ghost:   withShadow({ background: ghostBg, border: `1.5px solid ${green}`, color: isDark ? lighten(green, 20) : darken(green, 20), fontWeight: 700, borderRadius: 20 }),
      danger:  withShadow({ background: ghostBg, border: `1.5px solid ${red}`, color: red, fontWeight: 700, borderRadius: 20 }),
    };
  }

  // "solid" (default) — every variant is a genuinely filled button now:
  // primary keeps its gradient, ghost gets a solid neutral fill instead of
  // a near-invisible tint, and danger gets an actual solid red fill with
  // white text (the standard destructive-button treatment) instead of a
  // transparent/bordered chip that read as a status pill in disguise.
  return {
    primary: withShadow({ background: `linear-gradient(135deg,${yellow},${darken(yellow)})`, color: blueDark, border: "none", fontWeight: 800 }),
    ghost:   withShadow({ background: ghostSolidBg, color: ghostText, border: "none", fontWeight: 700 }),
    danger:  withShadow({ background: `linear-gradient(135deg,${red},${darken(red, 25)})`, color: "#fff", border: "none", fontWeight: 700 }),
  };
}

/**
 * Status pill style for one semantic tone, given the brand colors and style.
 * tone: "success" | "warning" | "danger" | "info" | "neutral"
 *
 * The flat/tinted/bordered family (solid/soft/outline) never has a border or
 * shadow, so a pill can never borrow a button's raised/clickable affordance.
 * The material-driven styles instead express "this is not a button" through
 * their OWN material's passive form — an inset (recessed) neumorphic
 * surface, a thin glass chip, a bordered geometric tag, etc. — never a
 * raised, shadowed one. Every style returns `dotColor` + `dotRadius` for the
 * small mark StatusBadge.js renders; `dotRadius` varies the mark's shape
 * (circle/square/organic) to match each style's own geometry.
 */
export function buildStatusPill({ tone, green, red, orange, blue, isDark, style }) {
  const NEUTRAL = isDark ? "#94a3b8" : "#64748b";
  const base = { success: green || "#059669", warning: orange || "#d97706",
                 danger: red || "#dc2626", info: blue || "#034e7b",
                 neutral: NEUTRAL }[tone] || NEUTRAL;
  const dotColor = isDark ? lighten(base, 15) : base;

  if (style === "neumorphism") {
    const bg = isDark ? "#1b2733" : "#eef1f4";
    const dark = isDark ? "rgba(0,0,0,0.5)" : "rgba(2,48,79,0.14)";
    const light = isDark ? "rgba(255,255,255,0.04)" : "#ffffff";
    return { background: bg, color: isDark ? lighten(base, 30) : base, dotColor, dotRadius: "50%",
             boxShadow: `inset 2px 2px 5px ${dark}, inset -2px -2px 5px ${light}` };
  }
  if (style === "glassmorphism") {
    return { background: `rgba(255,255,255,${isDark ? 0.08 : 0.5})`, color: isDark ? lighten(base, 30) : base, dotColor, dotRadius: "50%",
             border: `1px solid rgba(255,255,255,${isDark ? 0.14 : 0.7})`, backdropFilter: "blur(6px)" , WebkitBackdropFilter: "blur(6px)"};
  }
  if (style === "polymorphism") {
    return { background: "transparent", color: base, dotColor, dotRadius: "2px", border: `1px solid ${base}`, borderRadius: 6 };
  }
  if (style === "vintage") {
    return { background: `rgba(${hexToRgb(base)},0.08)`, color: base, dotColor, dotRadius: "2px", border: `1px solid ${base}`, borderRadius: 5 };
  }
  if (style === "gradient") {
    return { background: "linear-gradient(120deg, rgba(0,0,0,0.05), transparent)", color: base, dotColor, dotRadius: "50%", border: `1px solid ${base}` };
  }
  if (style === "nature") {
    return { background: `rgba(${hexToRgb(base)},0.10)`, color: base, dotColor, dotRadius: "40% 60% 60% 40% / 60% 40% 60% 40%" };
  }

  // Outline style keeps buttons transparent-bordered, but a status pill
  // still needs *some* fill to read as a label rather than plain text —
  // it just uses the lightest tint of the three presets.
  const alpha = style === "outline" ? 0.08 : style === "soft" ? 0.12 : 0.18;
  return {
    background: `rgba(${hexToRgb(base)},${alpha})`,
    color: isDark ? lighten(base, 30) : base,
    dotColor, dotRadius: "50%",
  };
}

export { hexToRgb, darken, lighten };
